/**
 * Share Prompt Throttle Utility
 * 
 * Shows "Please share this app" card at most once per calendar day.
 * Uses localStorage with in-memory fallback.
 */

const STORAGE_KEY = 'fsp_share_prompt_last_shown';

// In-memory guard for when localStorage is unavailable
let shownThisPageLoad = false;

/**
 * Get today's date as YYYY-MM-DD in local time
 */
export function getTodayStr() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if we can show the prompt today
 */
export function canShowToday() {
  // Already shown this page load - don't show again
  if (shownThisPageLoad) return false;
  
  try {
    const lastShown = localStorage.getItem(STORAGE_KEY);
    const today = getTodayStr();
    return lastShown !== today;
  } catch (e) {
    // localStorage unavailable - use in-memory guard only
    return !shownThisPageLoad;
  }
}

/**
 * Mark the prompt as shown for today
 */
export function markShownToday() {
  shownThisPageLoad = true;
  try {
    localStorage.setItem(STORAGE_KEY, getTodayStr());
  } catch (e) {
    // localStorage unavailable - in-memory guard is already set
  }
}

/**
 * Check if we should show share prompt, returns true if allowed
 * @param {string} trigger - What triggered this check (for future analytics)
 * @returns {boolean}
 */
export function shouldShowSharePrompt(trigger) {
  if (!canShowToday()) return false;
  shownThisPageLoad = true; // Mark as checked to prevent duplicate calls
  return true;
}

/**
 * Reset for testing purposes only
 */
export function _resetForTesting() {
  shownThisPageLoad = false;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // localStorage unavailable - nothing to clear
  }
}
